import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../services/api";

const PRESET_OPTIONS = [
  { value: "last_30_days", label: "Laatste 30 dagen" },
  { value: "last_90_days", label: "Laatste 90 dagen" },
  { value: "year_to_date", label: "Dit jaar tot nu" },
  { value: "custom", label: "Custom (voorbereid)" },
];

function buildParams({ preset, from, to }) {
  const params = { preset };
  if (preset === "custom") {
    if (from) params.from = from;
    if (to) params.to = to;
  }
  return params;
}

export default function KamAnalysePage() {
  const [preset, setPreset] = useState("last_30_days");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState({ monthly: [], weekly: [] });
  const [projects, setProjects] = useState([]);
  const [installations, setInstallations] = useState([]);

  const params = useMemo(() => buildParams({ preset, from, to }), [preset, from, to]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [overviewRes, trendsRes, projectsRes, installationsRes] = await Promise.all([
        api.get("/kam/analytics/overview", { params }),
        api.get("/kam/analytics/trends", { params }),
        api.get("/kam/analytics/projects", { params }),
        api.get("/kam/analytics/installations", { params }),
      ]);
      setOverview(overviewRes?.data || null);
      setTrends(trendsRes?.data || { monthly: [], weekly: [] });
      setProjects(Array.isArray(projectsRes?.data?.items) ? projectsRes.data.items : []);
      setInstallations(Array.isArray(installationsRes?.data?.items) ? installationsRes.data.items : []);
    } catch (err) {
      setError(err?.response?.data?.message || "KAM analyse laden mislukt");
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  const totals = overview?.totals || {};
  const trendSignals = Array.isArray(overview?.trendSignals) ? overview.trendSignals : [];

  return (
    <div className="space-y-4">
      <section className="rounded-xl border bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">KAM analyse</h2>
            <p className="text-sm text-gray-500">Veiligheid, trends en operationeel inzicht.</p>
          </div>
        </div>
      </section>

      <section className="grid gap-2 rounded-xl border bg-white p-4 shadow-sm md:grid-cols-5">
        <label className="text-xs font-medium text-gray-600">
          Periode
          <select
            value={preset}
            onChange={(event) => setPreset(event.target.value)}
            className="mt-1 w-full rounded border border-gray-300 px-2 py-2 text-sm"
          >
            {PRESET_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs font-medium text-gray-600">
          Van
          <input
            type="date"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
            disabled={preset !== "custom"}
            className="mt-1 w-full rounded border border-gray-300 px-2 py-2 text-sm disabled:bg-gray-100"
          />
        </label>
        <label className="text-xs font-medium text-gray-600">
          Tot
          <input
            type="date"
            value={to}
            onChange={(event) => setTo(event.target.value)}
            disabled={preset !== "custom"}
            className="mt-1 w-full rounded border border-gray-300 px-2 py-2 text-sm disabled:bg-gray-100"
          />
        </label>
        <div className="md:col-span-2 flex items-end">
          <button
            type="button"
            onClick={load}
            className="rounded bg-slate-800 px-3 py-2 text-sm text-white hover:bg-slate-900"
          >
            Vernieuwen
          </button>
        </div>
      </section>

      {error ? <section className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</section> : null}

      <section className="grid gap-3 md:grid-cols-4">
        <MetricCard label="Totaal meldingen" value={totals.totalReports} loading={loading} />
        <MetricCard label="Open meldingen" value={totals.openReports} loading={loading} />
        <MetricCard label="Critical meldingen" value={totals.criticalReports} loading={loading} tone="danger" />
        <MetricCard label="Overdue acties" value={totals.overdueActions} loading={loading} tone="warning" />
        <MetricCard label="Gem. oplostijd (uur)" value={totals.avgResolveHours} loading={loading} />
        <MetricCard label="Toolbox sessies" value={totals.toolboxSessionsExecuted} loading={loading} />
        <MetricCard label="Toolbox totaal" value={totals.toolboxSessionsTotal} loading={loading} />
        <MetricCard label="Bevestigingen" value={totals.toolboxConfirmations} loading={loading} />
      </section>

      <section className="grid gap-4 lg:grid-cols-2">
        <ListCard title="Meldingen per status" items={overview?.distributions?.byStatus} loading={loading} />
        <ListCard title="Meldingen per severity" items={overview?.distributions?.bySeverity} loading={loading} />
        <ListCard title="Meldingen per type" items={overview?.distributions?.byType} loading={loading} />
        <ListCard title="Per maand (trend)" items={trends?.monthly?.map((item) => ({ _id: `${item?._id?.year}-${item?._id?.month}`, count: item?.total || 0 }))} loading={loading} />
      </section>

      <section className="grid gap-4 lg:grid-cols-2">
        <TopRows title="Projecten" rows={projects} loading={loading} />
        <TopRows title="Installaties" rows={installations} loading={loading} />
      </section>

      <section className="rounded-xl border bg-white p-4 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-900">Trend signalering</h3>
        {loading ? <p className="mt-2 text-sm text-gray-500">Laden...</p> : null}
        {!loading && trendSignals.length === 0 ? <p className="mt-2 text-sm text-gray-500">Geen signalen.</p> : null}
        {!loading && trendSignals.length > 0 ? (
          <ul className="mt-2 space-y-2">
            {trendSignals.map((signal, index) => {
              const sev = String(signal?.severity || "");
              const cls = sev === "high" ? "border-red-200 bg-red-50 text-red-800" : "border-amber-200 bg-amber-50 text-amber-800";
              return (
                <li key={`${signal?.code || "signal"}-${index}`} className={`rounded border px-3 py-2 text-sm ${cls}`}>
                  <p className="font-semibold">{signal?.title || signal?.code}</p>
                  <p className="text-xs">waarde: {String(signal?.value ?? "-")}</p>
                </li>
              );
            })}
          </ul>
        ) : null}
      </section>
    </div>
  );
}

function MetricCard({ label, value, loading, tone = "default" }) {
  const cls =
    tone === "danger"
      ? "border-red-200 bg-red-50 text-red-800"
      : tone === "warning"
      ? "border-amber-200 bg-amber-50 text-amber-800"
      : "border-gray-200 bg-white text-gray-900";
  return (
    <article className={`rounded-xl border p-4 shadow-sm ${cls}`}>
      <p className="text-xs uppercase tracking-wide">{label}</p>
      <p className="mt-1 text-2xl font-semibold">{loading ? "..." : value ?? 0}</p>
    </article>
  );
}

function ListCard({ title, items, loading }) {
  const rows = Array.isArray(items) ? items : [];
  return (
    <section className="rounded-xl border bg-white p-4 shadow-sm">
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      {loading ? <p className="mt-2 text-sm text-gray-500">Laden...</p> : null}
      {!loading && rows.length === 0 ? <p className="mt-2 text-sm text-gray-500">Geen data.</p> : null}
      {!loading && rows.length > 0 ? (
        <ul className="mt-2 space-y-2 text-sm">
          {rows.map((row, index) => (
            <li key={`${String(row?._id || "x")}-${index}`} className="flex items-center justify-between rounded border border-gray-100 px-2 py-1">
              <span className="truncate text-gray-700">{String(row?._id || "-")}</span>
              <span className="font-semibold text-gray-900">{Number(row?.count || 0)}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function TopRows({ title, rows, loading }) {
  const items = Array.isArray(rows) ? rows : [];
  return (
    <section className="rounded-xl border bg-white p-4 shadow-sm">
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      {loading ? <p className="mt-2 text-sm text-gray-500">Laden...</p> : null}
      {!loading && items.length === 0 ? <p className="mt-2 text-sm text-gray-500">Geen data.</p> : null}
      {!loading && items.length > 0 ? (
        <ul className="mt-2 space-y-2 text-sm">
          {items.map((row) => (
            <li key={row?._id || row?.label} className="rounded border border-gray-100 p-2">
              <div className="flex items-center justify-between">
                <p className="font-medium text-gray-800">{row?.label || "-"}</p>
                <p className="font-semibold text-gray-900">{Number(row?.total || row?.count || 0)}</p>
              </div>
              <p className="text-xs text-gray-500">open: {Number(row?.open || 0)} • critical: {Number(row?.critical || 0)}</p>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
